<script lang="ts">
	import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
	//Services
	import { format } from 'svelte-i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	//Stores
	import {
		getDeviceStatus,
		getDeviceSettings,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	import { app } from 'src/stores/session/native-bridge';
	//Helpers
	import { real_id } from '../../../../helpers/interface_helpers';
	import { onSettingsResponse } from '../../../../helpers/device_settings_helpers';
	import { executeModeReplacement } from '../../../../helpers/mode_switch_helpers';
	import {
		checkSharedChannels,
		unshareAllChannels,
		type SharedChannelData,
	} from '../../../../helpers/profile_change_shared_helper';
	import compareVersions from 'compare-versions';
	//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	import { Alert, Button, ButtonGroupV2, Container, Loader } from '@Shelly/shelly-design-system';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	import { resetDrawer, resetDrawerClose } from 'src/helpers/drawer_helpers';
	//Components
	import ProfileChangeSharedModal from './ProfileChangeSharedModal.svelte';
	import DeviceProfileHelp from './DeviceProfileHelp.svelte';
	import SettingGuide from '../../SettingGuide.svelte';

	import { isDevServerUser } from 'src/helpers/user_server_conditions';

	export let device;

	const id = real_id(device.id);
	$: settingsStore = getDeviceSettings(id);
	let settings = readDeviceSettings(id);
	$: settings = $settingsStore ?? settings;

	let options: string[];
	let i18nOptions: string[];
	let value: string;
	let cache: string;
	let loading: boolean = false;
	let showModal: boolean = false;
	let sharedChannelsData: SharedChannelData[] = [];

	const minVersion = '1.1.0';

	$: fwVersion = settings?.DeviceInfo?.ver.split('-')[0];

	$: checkMinVersion = fwVersion && compareVersions(fwVersion, minVersion) > -1;

	const handleSave: () => Promise<void> = async () => {
		if (cache === value) return;

		const channelsData = await checkSharedChannels(device);

		if (channelsData.length > 0) {
			sharedChannelsData = channelsData;
			showModal = true;
			return;
		}

		await sendReq();
	};

	const sendReq: () => Promise<void> = async () => {
		if (cache === value) return;
		loading = true;
		showModal = false;

		await unshareAllChannels(sharedChannelsData);

		const params = {
			id: device.channel,
			name: options[i18nOptions.indexOf(value as string)],
		};

		const res: SettingRequest = await gen2Request(device, 'Shelly.SetProfile', params);

		const response = onSettingsResponse(res, device, undefined, true);

		if (response) {
			const profile = options.indexOf(value.toLowerCase());
			settings.sys.device.profile = options[profile];

			if (options[profile] === 'monophase') {
				await executeModeReplacement(device, 'monophase', 'mode', true);
			} else {
				await executeModeReplacement(device, 'triphase', 'mode', false);
			}

			devices_settings.remove(id);
			devices_status.remove(id);

			if ($app) {
				resetDrawerClose();
			} else {
				resetDrawer();
			}
		}
		value = cache;
		loading = false;
	};

	const getData: () => Promise<string[]> = async () => {
		const getProfiles = await gen2Request(device, 'shelly.listprofiles', {});

		options =
			typeof (getProfiles as any)?.data?.profiles !== 'undefined'
				? Object.keys((getProfiles as any).data.profiles)
				: typeof (getProfiles as any)?.result !== 'undefined'
					? Object.keys((getProfiles as any).result.profiles)
					: ['monophase', 'triphase'];
		i18nOptions = [
			$format(`option_${options[0]}`, { default: options[0] }),
			$format(`option_${options[1]}`, { default: options[1] }),
		];

		value = i18nOptions[options.indexOf(settings?.sys?.device?.profile)];

		cache = value;
		return i18nOptions;
	};

	$: selectedProfile = options?.[i18nOptions?.indexOf(value)] ?? '';
	$: isSaveDisabled = loading || cache === value;
	$: canEditProfile = checkMinVersion || isDevServerUser(['repo', 't1', 'nb']);

	$: if (options && i18nOptions && settings?.sys?.device?.profile && cache === value) {
		const idx = options.indexOf(settings.sys.device.profile);
		if (idx !== -1) {
			value = i18nOptions[idx];
			cache = i18nOptions[idx];
		}
	}

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout && canEditProfile) {
			stickyBottomAction.set({
				onClick: handleSave,
				loading,
				disabled: isSaveDisabled,
				icon: 'fas fa-save',
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

{#if checkMinVersion || isDevServerUser(['repo', 't1', 'nb'])}
	<SettingGuide
		text={$format('settings_guide_device_profile', {
			default:
				'Choose how this device should operate. Changing the profile restarts the device and clears all existing schedules and actions.',
		})}
	/>
	{#await getData()}
		<Loader />
	{:then _}
		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<ButtonGroupV2
				class="mb-s"
				id="buttongroup-pro3em-profile"
				aria-label={$format('device_profile', { default: 'Device profile' })}
				fullWidth
				noWrap
				orientation="horizontal"
			>
				{#each i18nOptions as option}
					<Button
						class={value === option ? 'active' : undefined}
						size="m"
						on:click={() => (value = option)}
					>
						{option}
					</Button>
				{/each}
			</ButtonGroupV2>
			<DeviceProfileHelp profile={selectedProfile} />
		</Container>
	{/await}

	{#if sharedChannelsData.length > 0}
		<ProfileChangeSharedModal bind:showModal on:confirm={sendReq} />
	{/if}

	{#if !$showNewLayout}
		<Button class="mt-s" {loading} size="m" width="full" on:click={handleSave}
			>{$format('button_save')}</Button
		>
	{/if}
{:else}
	<Alert
		type="warning"
		heading="Setting requires specific Beta firmware"
		message="Required version 1.1.0-beta and above"
		class="mb-s"
	/>
{/if}
